// GuidedTour — mounts at Layout level, drives the Driver.js tour lifecycle.
//
// The driver instance is held at module level so any component can call
// advanceTour() to programmatically advance past an interactive step without
// prop-drilling or a React context.
//
// Step map (0-indexed):
//   Navigator phase  — 0: welcome  1: sidebar  2: project panel
//                      3: upload zone  4: paper grid
//                      5: select paper (interactive)  6: go-to-review (interactive)
//   PaperReview phase — 7: return btn  8: transcript btn (interactive)
//                       9: transcript panel  10: scan  11: filter toolbar
//                       12: cards  13: grade (interactive)
//                       14: score  15: export
//   Final            — 16: done

import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { driver, type Driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";

import { useTourStore } from "../state/tourStore";

import "./GuidedTour.css";

// ── Module-level driver singleton ─────────────────────────────────────────────

let _driver: Driver | null = null;

/** Advance past the current interactive step. Call from any onClick handler. */
export function advanceTour() {
  _driver?.moveNext();
}

/** Destroy the tour from anywhere (e.g. Settings cleanup). */
export function endTour() {
  _driver?.destroy();
}

// ── Step indices for route transitions ────────────────────────────────────────

const REVIEW_START_STEP = 7;
const FINAL_STEP = 16;

// ── Component ─────────────────────────────────────────────────────────────────

export function GuidedTour() {
  const { t } = useTranslation();
  const active = useTourStore((s) => s.active);
  const projectId = useTourStore((s) => s.projectId);

  const end = useTourStore((s) => s.end);
  const navigate = useNavigate();
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);

  function buildSteps(): DriveStep[] {
    return [
      // ── 0: Welcome — landscape modal with logo + wordmark ────────────────
      {
        popover: {
          title: "",
          description: t("tour.intro_desc"),
          align: "center",
          showButtons: ["next"],
          nextBtnText: t("tour.intro_btn"),
          popoverClass: "ophelia-tour-popover ophelia-tour-popover--welcome",
        },
      },
      // ── 1: Sidebar (info — disable interaction to prevent accidental clicks) ─
      {
        element: '[data-tour="sidebar"]',
        disableActiveInteraction: true,
        popover: {
          title: t("tour.sidebar_title"),
          description: t("tour.sidebar_desc"),
          side: "right",
          align: "start",
          showButtons: ["next"],
        },
      },
      // ── 2: Project panel (info — disable interaction) ─────────────────────
      {
        element: '[data-tour="details-panel"]',
        disableActiveInteraction: true,
        popover: {
          title: t("tour.project_panel_title"),
          description: t("tour.project_panel_desc"),
          side: "left",
          align: "start",
          showButtons: ["next", "previous"],
        },
      },
      // ── 3: Upload zone (info — disable interaction) ────────────────────────
      {
        element: '[data-tour="upload-zone"]',
        disableActiveInteraction: true,
        popover: {
          title: t("tour.upload_title"),
          description: t("tour.upload_desc"),
          side: "right",
          align: "start",
          showButtons: ["next", "previous"],
        },
      },
      // ── 4: Paper grid (info — disable interaction) ────────────────────────
      {
        element: '[data-tour="paper-grid"]',
        disableActiveInteraction: true,
        popover: {
          title: t("tour.paper_grid_title"),
          description: t("tour.paper_grid_desc"),
          side: "right",
          align: "start",
          showButtons: ["next", "previous"],
        },
      },
      // ── 5: Select paper row (interactive) ─────────────────────────────────
      {
        element: '[data-tour="paper-row-in-review"]',
        disableActiveInteraction: false,
        popover: {
          title: t("tour.select_paper_title"),
          description: t("tour.select_paper_desc"),
          side: "left",
          align: "start",
          disableButtons: ["next", "previous"],
        },
      },
      // ── 6: Go to Review (interactive — Kim clicks the real button) ─────────
      {
        element: '[data-tour="go-to-review-btn"]',
        disableActiveInteraction: false,
        onHighlightStarted: (el) => {
          // Button is deep in a scrollable panel — scroll into view synchronously
          // before driver calculates the spotlight rectangle.
          el?.scrollIntoView({ behavior: "instant", block: "center" });
        },
        popover: {
          title: t("tour.go_to_review_title"),
          description: t("tour.go_to_review_desc"),
          side: "left",
          align: "center",
          disableButtons: ["next", "previous"],
        },
      },
      // ── 7: Return button (info) ────────────────────────────────────────────
      {
        element: '[data-tour="return-btn"]',
        disableActiveInteraction: true,
        popover: {
          title: t("tour.return_btn_title"),
          description: t("tour.return_btn_desc"),
          side: "right",
          align: "start",
          showButtons: ["next"],
        },
      },
      // ── 8: Transcript button (interactive — Kim clicks to open transcript) ─
      {
        element: '[data-tour="transcript-btn"]',
        disableActiveInteraction: false,
        popover: {
          title: t("tour.transcript_title"),
          description: t("tour.transcript_desc"),
          side: "bottom",
          align: "center",
          disableButtons: ["next", "previous"],
        },
      },
      // ── 9: Transcript panel (info — visible after Kim opens it) ───────────
      {
        element: '[data-tour="transcript-panel"]',
        disableActiveInteraction: true,
        popover: {
          title: t("tour.transcript_panel_title"),
          description: t("tour.transcript_panel_desc"),
          side: "right",
          align: "start",
          showButtons: ["next", "previous"],
        },
      },
      // ── 10: Scan canvas (info) ─────────────────────────────────────────────
      {
        element: '[data-tour="scan-canvas"]',
        disableActiveInteraction: true,
        popover: {
          title: t("tour.scan_title"),
          description: t("tour.scan_desc"),
          side: "right",
          align: "start",
          showButtons: ["next", "previous"],
        },
      },
      // ── 11: Filter toolbar (info) ──────────────────────────────────────────
      {
        element: '[data-tour="filter-toolbar"]',
        disableActiveInteraction: true,
        popover: {
          title: t("tour.filter_title"),
          description: t("tour.filter_desc"),
          side: "left",
          align: "start",
          showButtons: ["next", "previous"],
        },
      },
      // ── 12: Annotation cards (info) ────────────────────────────────────────
      {
        element: '[data-tour="annotation-cards"]',
        disableActiveInteraction: true,
        popover: {
          title: t("tour.cards_title"),
          description: t("tour.cards_desc"),
          side: "left",
          align: "start",
          showButtons: ["next", "previous"],
        },
      },
      // ── 13: First annotation card (interactive) ────────────────────────────
      {
        element: '[data-tour="first-annotation"]',
        disableActiveInteraction: false,
        popover: {
          title: t("tour.grade_title"),
          description: t("tour.grade_desc"),
          side: "left",
          align: "start",
          disableButtons: ["next", "previous"],
        },
      },
      // ── 14: Score display (info) ───────────────────────────────────────────
      {
        element: '[data-tour="score-display"]',
        popover: {
          title: t("tour.score_title"),
          description: t("tour.score_desc"),
          side: "left",
          align: "start",
          showButtons: ["next", "previous"],
        },
      },
      // ── 15: Export button (info) ───────────────────────────────────────────
      {
        element: '[data-tour="export-btn"]',
        popover: {
          title: t("tour.export_title"),
          description: t("tour.export_desc"),
          side: "top",
          align: "center",
          showButtons: ["next", "previous"],
        },
      },
      // ── 16: Final ─────────────────────────────────────────────────────────
      {
        popover: {
          title: t("tour.final_title"),
          description: t("tour.final_desc"),
          align: "center",
          showButtons: ["next"],
          doneBtnText: t("tour.btn_done"),
        },
      },
    ];
  }

  // ── Start / stop driver when tour activates ───────────────────────────────

  useEffect(() => {
    if (!active) {
      _driver?.destroy();
      _driver = null;
      return;
    }

    // Navigate to the tour project so the paper grid is visible from step 1.
    if (projectId) {
      navigate(`/?project=${projectId}`);
    }

    // Brief delay so the project navigation settles before driver highlights.
    const timer = setTimeout(() => {
      const d = driver({
        allowClose: false,
        overlayOpacity: 0.6,
        stagePadding: 6,
        stageRadius: 8,
        popoverClass: "ophelia-tour-popover",
        showProgress: true,
        progressText: "{{current}} / {{total}}",
        steps: buildSteps(),
        onCloseClick: (_el, _step, { driver: dInstance }) => {
          window.sessionStorage.removeItem("ophelia.welcome.dismissed");
          dInstance.destroy();
          navigate("/");
        },
        onPopoverRender: (popover, { state }) => {
          // driver.js calls button.focus() after onPopoverRender returns, so
          // we must blur in a rAF (fires after that call) and also set
          // tabindex="-1" so the browser never auto-focuses them itself.
          [popover.nextButton, popover.previousButton].forEach((btn) => {
            btn.setAttribute("tabindex", "-1");
          });
          requestAnimationFrame(() => {
            (document.activeElement as HTMLElement | null)?.blur?.();
          });

          // Welcome step (0): inject logo + wordmark above the description.
          if (state.activeIndex === 0) {
            const logoRow = document.createElement("div");
            logoRow.className = "ophelia-tour-logo-row";
            logoRow.innerHTML =
              `<img src="/logo.svg" alt="" class="ophelia-tour-logo" />` +
              `<span class="ophelia-tour-wordmark">ophelia</span>`;
            popover.wrapper.insertBefore(logoRow, popover.description);
          }
        },
        onDestroyed: () => {
          _driver = null;
          end();
        },
      });
      _driver = d;
      d.drive(0);
    }, 350);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // ── Re-drive at correct step on route changes ─────────────────────────────

  useEffect(() => {
    if (!active || !_driver) return;

    const prev = prevPathnameRef.current;
    const curr = location.pathname;
    prevPathnameRef.current = curr;

    const wasReview = prev.startsWith("/review/");
    const isNowReview = curr.startsWith("/review/");

    if (!wasReview && isNowReview) {
      // Navigator → PaperReview: start review phase.
      const timer = setTimeout(() => _driver?.drive(REVIEW_START_STEP), 400);
      return () => clearTimeout(timer);
    }

    if (wasReview && !isNowReview) {
      // PaperReview → Navigator (e.g. Kim navigated back mid-tour): jump to final.
      const timer = setTimeout(() => _driver?.drive(FINAL_STEP), 400);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, active]);

  return null;
}
